/**
 * Adapter: PL `ACCORDION` widget → the accordion rows the legacy PDP "product details" section renders.
 *
 * `ACCORDION` is a pure container — its own `accordionData` carries no row content. Each row arrives as a
 * nested `HTML` child widget (`data.htmlData = { title, text }`, `config.defaultExpanded`), where `text`
 * is a raw HTML string. This adapter flattens those children into plain rows the widget maps over. Pure
 * (no hooks / no React) so it is unit-testable against the BE JSON.
 */

import LAYOUT_TYPES from '@src/quince-site-common/src/constants/layoutTypes'
import CustomSection from '@src/quince-site-common/src/domains/CustomSection'
import CUSTOM_SECTION_TYPE from '@src/quince-site-common/src/domains/CustomSectionType'
import type RichText from '@src/quince-site-common/src/domains/RichText'
import { getSvdContentAttribute, SVD_CONTENT_ATTR } from '@src/quince-site-common/src/helpers/svd-content-fields'
import {
  isSvdVariantScopedAttribute,
  type SvdAttributeReadable,
  type SvdVariantReadable,
} from '@src/quince-site-common/src/helpers/svd-custom-fields'
import { tabIds } from '@src/quince-site-pdp/src/organisms/productPageSections/product-details-tabs/constants'

import { toRichText } from '../widget-utils'

import type {
  AccordionWidgetData,
  HtmlWidgetData,
  WidgetConfig,
} from '@lastbrand/storefront-proto-registry/api/v1/widget'
import type { TFunction } from 'next-i18next'

export interface AccordionItem {
  /** Stable id used as the row key + DOM id (read-more height measurement keys off it). */
  id: string
  /** Accordion header label. */
  title: string
  /** Raw HTML body string (sanitized downstream by the legacy row via getSanitizedHtml). */
  html: string
  /** Whether the row starts expanded; undefined lets the legacy first-tab-open default apply. */
  defaultExpanded?: boolean
  /**
   * Pre-resolved `click__tabs__tab` tracker id. Only the SVD rows set it — they are built from typed
   * attribute keys, so the tab type is known up front and needs no locale-dependent title lookup. HTML-child
   * rows leave it undefined and keep resolving through `toDetailsTabTracker(title, id)` exactly as before.
   */
  tracker?: string
}

/**
 * Rows resolved upstream by the widget shell (`index.tsx`), which picks between the ACCORDION's own HTML
 * children and the SVD attribute fallback. Optional so each leaf still flattens the children on its own when
 * rendered directly (tests, any future direct call site).
 */
export interface AccordionItemsProp {
  items?: AccordionItem[]
}

/**
 * Minimal shape of a nested child widget. `Widget.widgets` is a recursive proto type that can't be read
 * cleanly through `Omit<Widget,'data'>`, so the widget reads it as `unknown` and this narrows it.
 */
interface AccordionNestedWidget {
  id?: string
  widgetId?: string
  data?: { htmlData?: HtmlWidgetData }
  config?: WidgetConfig
}

/**
 * The EXACT non-PL `click__tabs__tab` tracker id per Details & Care tab type, copied verbatim from
 * `product-details-tabs/utils.ts` — including non-PL's own inconsistent spelling (hyphens in
 * `size-and-fit`, underscores in `shelf_life` / `more_information`), so PL emits byte-for-byte the same
 * `value` non-PL does. Keyed by the row header title normalised to lowercase single-spaced words
 * (`&`→`and`); `toDetailsTabTracker` first tries the row's locale-independent `widgetId` slug and only
 * then falls back to this title key.
 */
/* eslint-disable i18next/no-literal-string -- telemetry tracker ids, not user-facing copy */
const DETAILS_TAB_TRACKERS: Record<string, string> = {
  details: 'product-description_details',
  'size and fit': 'product-description_size-and-fit',
  care: 'product-description_care',
  benefits: 'product-description_benefits',
  ingredients: 'product-description_ingredients',
  notes: 'product-description_notes',
  installation: 'product-description_installation',
  specs: 'product-description_specs',
  dimensions: 'product-description_dimensions',
  feeding: 'product-description_feeding',
  'shelf life': 'product-description_shelf_life',
  shipping: 'product-description_shipping',
  'more information': 'product-description_more_information',
}
/* eslint-enable i18next/no-literal-string */

// BE row ids are locale-INDEPENDENT semantic slugs — `widget-pdp-html-<slug>-<hash>` (observed in prod
// payloads: `widget-pdp-html-details-…`, `widget-pdp-html-size-fit-…`, `widget-pdp-html-care-…`,
// `widget-pdp-html-notes-…`). The slug is the tab type, so it resolves the tracker on ANY locale where
// the localized title would miss. The alias below covers the one confirmed slug spelling that differs
// from the title's own words; every other observed slug normalizes straight into the title-keyed map.
/* eslint-disable i18next/no-literal-string -- telemetry id fragments, not user-facing copy */
const DETAILS_ROW_WIDGET_ID_PREFIX = 'widget-pdp-html-'
const DETAILS_SLUG_ALIASES: Record<string, string> = {
  'size fit': 'size and fit',
}
/* eslint-enable i18next/no-literal-string */

/** Lowercase single-spaced words, `&`→`and` — the shared key normalizer for titles and id slugs. */
function toTrackerKey(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

/**
 * Resolve the exact non-PL `click__tabs__tab` tracker id for a Details & Care accordion row, so the
 * shared `ProductDetailsAndCareV2` fires `click__tabs__tab` on expand (it no-ops on an empty tracker —
 * which is why the PL rows, previously passing `tracker: ''`, never emitted the event).
 *
 * Resolution order — locale-independent first:
 * 1. `widgetId` slug (`widget-pdp-html-<slug>-<hash>`): the BE's semantic tab type, immune to the
 *    row title's localisation, so non-English PDPs still emit the event.
 * 2. The row header title, normalised — covers rows whose id lacks the slug shape (older payloads,
 *    title-fallback ids).
 *
 * Returns `''` when neither resolves (blank rows, non-standard types) → the shared component stays
 * silent rather than emit a WRONG id. This matches non-PL semantics: non-PL only models these fixed tab
 * types, so a row it doesn't recognise wouldn't fire there either.
 */
export function toDetailsTabTracker(title: string, widgetId?: string): string {
  return DETAILS_TAB_TRACKERS[resolveDetailsRowKey(title, widgetId)] ?? ''
}

/**
 * The canonical Details & Care ROW KEY for an accordion row — the shared key both the tracker map and the
 * SVD attribute map are indexed by, resolved with the same locale-independent-first order documented on
 * `toDetailsTabTracker`: the `widget-pdp-html-<slug>-<hash>` slug when it names a row type we model, then
 * the normalised row title.
 *
 * Factored out so `toDetailsTabTracker` and `withSvdVariantContent` cannot drift: a row whose telemetry
 * resolves as `details` MUST read its per-variant copy from the `details` attribute, and one function
 * deciding both is the only way to guarantee that.
 */
function resolveDetailsRowKey(title: string, widgetId?: string): string {
  if (widgetId?.startsWith(DETAILS_ROW_WIDGET_ID_PREFIX)) {
    // `<slug>-<hash>`: the hash is a hex suffix; strip it when present so pure-slug ids also resolve.
    const slug = widgetId.slice(DETAILS_ROW_WIDGET_ID_PREFIX.length).replace(/-[0-9a-f]{6,}$/, '')
    const slugKey = toTrackerKey(slug)
    const aliased = DETAILS_SLUG_ALIASES[slugKey] ?? slugKey
    // Only accept a slug we actually model; otherwise fall through to the title, exactly as before.
    if (DETAILS_TAB_TRACKERS[aliased]) return aliased
  }
  return toTrackerKey(title)
}

/**
 * A body is "blank" when it has no real content. The rich-text editor emits `<p><br></p>` for an empty
 * field — a non-empty string that renders nothing. Non-PL treats this exact value as empty via
 * `getFormattedCustomFieldText` and only builds a details tab when the result is truthy, so an empty
 * "Size & Fit" is hidden. Mirror that here (same sentinel) so PL drops the same blank rows. STRFRNT-15991.
 */
const EMPTY_RICH_TEXT_HTML = '<p><br></p>'
function isBlankBody(text: string): boolean {
  const trimmed = text.trim()
  return trimmed === '' || trimmed === EMPTY_RICH_TEXT_HTML
}

/** Flatten the ACCORDION's nested HTML children into rows, dropping any child with a blank body. */
export function toAccordionItems(widgets: unknown): AccordionItem[] {
  const nested: AccordionNestedWidget[] = Array.isArray(widgets) ? (widgets as AccordionNestedWidget[]) : []

  return nested
    .map((widget): AccordionItem | null => {
      const html = widget.data?.htmlData
      const title = html?.title ?? ''
      const text = html?.text ?? ''
      // Skip sections whose body is empty (incl. the editor's empty `<p><br></p>`) so the accordion
      // never renders a blank row like the sofa's empty "Size & Fit" — matching non-PL. STRFRNT-15991.
      if (isBlankBody(text)) return null
      return {
        id: widget.widgetId || widget.id || title,
        title,
        html: text,
        defaultExpanded: widget.config?.defaultExpanded,
      }
    })
    .filter((item): item is AccordionItem => Boolean(item))
}

/**
 * SVD details rows, in non-PL order — one entry per `getCommonTabsConfig` / `getProductDetailsTabsConfig`
 * tab, so an SVD PDP renders the same rows, in the same sequence, under the same headers and DOM ids as the
 * non-PL PDP does from `product.details` / `.sizeAndFit` / `.careAndMaintenance` / ….
 *
 * `id` reuses non-PL's `tabIds` (anchor + read-more measurement parity), `titleKey` reuses the exact i18n
 * keys `getDetailsAndCareConfig` uses (identical copy in every locale), and `trackerKey` indexes the SAME
 * `DETAILS_TAB_TRACKERS` map the HTML-child path resolves through — so telemetry ids cannot drift between
 * the two row sources. `attrKeys` is tried in order, first non-empty wins (CARE is carried twice).
 */
/* eslint-disable i18next/no-literal-string -- i18n key names + telemetry map keys, not display copy */
const SVD_DETAILS_ROWS: { id: string; titleKey: string; trackerKey: string; attrKeys: string[] }[] = [
  {
    id: tabIds.PRODUCT_DETAILS,
    titleKey: 'details_v1',
    trackerKey: 'details',
    attrKeys: [SVD_CONTENT_ATTR.DETAILS],
  },
  {
    id: tabIds.SIZEANDFIT,
    titleKey: 'size_fit',
    trackerKey: 'size and fit',
    attrKeys: [SVD_CONTENT_ATTR.SIZE_AND_FIT],
  },
  {
    id: tabIds.CARE,
    titleKey: 'care',
    trackerKey: 'care',
    // Both keys carry the same copy in the observed payload; `careAndMaintenance` is the one non-PL's CARE
    // tab reads, so it wins and `websiteCareInstructions` is the fallback.
    attrKeys: [SVD_CONTENT_ATTR.CARE_AND_MAINTENANCE, SVD_CONTENT_ATTR.WEBSITE_CARE_INSTRUCTIONS],
  },
  { id: tabIds.BENEFITS, titleKey: 'benefits', trackerKey: 'benefits', attrKeys: [SVD_CONTENT_ATTR.BENEFITS] },
  {
    id: tabIds.INGREDIENTS,
    titleKey: 'ingredients',
    trackerKey: 'ingredients',
    attrKeys: [SVD_CONTENT_ATTR.INGREDIENTS],
  },
  { id: tabIds.NOTES, titleKey: 'notes', trackerKey: 'notes', attrKeys: [SVD_CONTENT_ATTR.NOTES] },
  {
    id: tabIds.INSTALLATION,
    titleKey: 'installation',
    trackerKey: 'installation',
    attrKeys: [SVD_CONTENT_ATTR.INSTALLATION],
  },
  { id: tabIds.SPECS, titleKey: 'specs', trackerKey: 'specs', attrKeys: [SVD_CONTENT_ATTR.SPECS] },
  { id: tabIds.DIMENSIONS, titleKey: 'dimensions', trackerKey: 'dimensions', attrKeys: [SVD_CONTENT_ATTR.DIMENSIONS] },
  { id: tabIds.FEEDING, titleKey: 'feeding', trackerKey: 'feeding', attrKeys: [SVD_CONTENT_ATTR.FEEDING] },
  {
    id: tabIds.SHELF_LIFE,
    titleKey: 'shelf_life',
    trackerKey: 'shelf life',
    attrKeys: [SVD_CONTENT_ATTR.SHELF_LIFE],
  },
  { id: tabIds.SHIPPING, titleKey: 'shipping', trackerKey: 'shipping', attrKeys: [SVD_CONTENT_ATTR.SHIPPING] },
  {
    id: tabIds.MORE_INFORMATION,
    titleKey: 'more_information',
    trackerKey: 'more information',
    attrKeys: [SVD_CONTENT_ATTR.MORE_INFORMATION],
  },
]
/* eslint-enable i18next/no-literal-string */

/**
 * SVD fallback rows: build the accordion straight from the SVD purchase payload's content attributes.
 *
 * The V2 (SVD) `ACCORDION` arrives as an EMPTY container — `accordionData: {}` with no nested `HTML`
 * children — because the V2 contract ships this copy as product-level `attributeValues` RICH_TEXT entries on
 * `PRODUCT_PURCHASE_V2` instead. `toAccordionItems` therefore returns `[]` and the widget renders `null`, so
 * an SVD PDP loses its whole "product details" section. This rebuilds those rows from the typed attributes.
 *
 * SVD-ONLY: the caller passes the sibling `PRODUCT_PURCHASE_V2` widget's data, which exists ONLY on a V2
 * payload. On any non-SVD page there is no such widget, the source is `undefined`, and this returns `[]` —
 * the fallback cannot fire. It is also strictly a FALLBACK: the caller prefers real HTML children whenever
 * the BE sends them, so a future payload that ships both keeps the BE-authored rows.
 *
 * `variant` is the RESOLVED variant (the widget reads it out of the payload's `hydratedVariants` by the
 * buy box's published `selectedVariantId` — see `index.tsx`). It matters because real payloads scope
 * `details` / `websiteCareInstructions` to the VARIANT (`attributeMap.variant`): without it those rows read
 * empty and vanish, and with it they re-resolve on every selection change. Product-level copy remains the
 * fallback for a key the resolved variant does not author (`getSvdContentAttribute`).
 */
export function toSvdAccordionItems(
  source: SvdAttributeReadable | undefined,
  t: TFunction,
  variant?: SvdVariantReadable,
): AccordionItem[] {
  if (!source) return []

  return SVD_DETAILS_ROWS.flatMap((row): AccordionItem[] => {
    const html = getSvdContentAttribute(source, row.attrKeys, variant)
    // Same blank rule as the HTML-child path — an empty rich-text field must not draw a headed, bodyless
    // row (non-PL only builds a tab when the formatted text is truthy). STRFRNT-15991.
    if (!html || isBlankBody(html)) return []
    return [
      {
        id: row.id,
        title: t(row.titleKey),
        html,
        tracker: DETAILS_TAB_TRACKERS[row.trackerKey] ?? '',
      },
    ]
  })
}

/**
 * Row key → the SVD content attribute keys that row's body lives in, derived from the SAME
 * `SVD_DETAILS_ROWS` table the attribute-only fallback is built from. One table, so an HTML-child row and a
 * fallback row for the same tab can never read different attributes.
 */
const DETAILS_ROW_ATTR_KEYS: Record<string, string[]> = Object.fromEntries(
  SVD_DETAILS_ROWS.map(row => [row.trackerKey, row.attrKeys]),
)

/**
 * Re-resolve BE-authored HTML rows against the RESOLVED VARIANT, so a variant-scoped row follows the
 * shopper's selection instead of staying frozen on whatever the server baked in.
 *
 * WHY THIS EXISTS. The V2 payload ships the accordion BOTH ways: real nested `HTML` children AND the same
 * copy as `attributeValues` on `PRODUCT_PURCHASE_V2`. `attributeMap` scopes `details` / `shipping` /
 * `websiteCareInstructions` to the VARIANT, and the HTML child's `text` is the server's pre-render of the
 * INITIALLY resolved variant only (in the captured payload the DETAILS child is `<p>Black Color</p>` —
 * verbatim `hydratedVariants[selection.variantId].attributeValues.details`). `toAccordionItems` is a pure
 * function of those static children, so without this overlay the row keeps showing the first variant's copy
 * for every other variant — Ash Grey's `<p>Ash Grey Color</p>` never appears.
 *
 * SCOPE-GATED, deliberately narrow. Only a row whose attribute is VARIANT-scoped per `attributeMap` is
 * re-resolved. A product-scoped row (`sizeAndFit` in the captured payload) is identical for every variant, so
 * there is nothing to follow and the BE-authored child keeps owning it — the existing "children win"
 * precedence stands everywhere it was actually correct, and this fixes only the case it got wrong.
 *
 * Within a variant-scoped row the attribute chain (`getSvdContentAttribute`: variant first, product-level
 * fallback) WINS over the baked HTML whenever it resolves to real copy, because it is the only source that
 * knows which variant is selected. The static `text` remains the last fallback, for a row the attributes
 * don't model (`DETAILS_ROW_ATTR_KEYS` miss) or whose chain is blank — losing BE-authored content would be a
 * worse failure than showing slightly stale copy.
 *
 * NON-SVD IS UNTOUCHED: `source` is the sibling `PRODUCT_PURCHASE_V2` payload, absent on every non-SVD page,
 * and this returns `items` by reference when it is missing. Rows whose copy does not change are returned by
 * reference too, so the read-more height measurement downstream only re-keys on rows that actually changed.
 */
export function withSvdVariantContent(
  items: AccordionItem[],
  source: SvdAttributeReadable | undefined,
  variant?: SvdVariantReadable,
): AccordionItem[] {
  if (!source) return items

  return items.map(item => {
    const attrKeys = DETAILS_ROW_ATTR_KEYS[resolveDetailsRowKey(item.title, item.id)]
    // Scope gate: only re-resolve keys the payload itself declares variant-scoped.
    const variantKeys = attrKeys?.filter(key => isSvdVariantScopedAttribute(source, key))
    if (!variantKeys?.length) return item
    const html = getSvdContentAttribute(source, variantKeys, variant)
    // `getSvdContentAttribute` already normalises the editor-empty sentinel to `''`; the isBlankBody check
    // keeps this honest if that ever changes. Identity is preserved when the copy is unchanged.
    if (!html || isBlankBody(html) || html === item.html) return item
    return { ...item, html }
  })
}

/**
 * Adapt the ACCORDION's own `accordionData.airlineCompatibilitySection` (present on luggage) into the
 * `CustomSection` the legacy `AirlineCompatibility` modal consumes — matching the non-PL ProductForm
 * path (which filters `product.*Sections` for AIRLINE_COMPATIBILITY_LISTING and feeds the same atom).
 * The modal is opened by the `#airline-compatibility` link inside the DETAILS HTML this widget renders.
 * Returns undefined when there's no section or no airline listing to show.
 */
export function toAirlineCompatibilitySection(data?: AccordionWidgetData): CustomSection | undefined {
  const section = data?.airlineCompatibilitySection
  if (!section || section.airlineCompatibilityListing.length === 0) return undefined

  const richTexts = section.richTexts.map(rt => toRichText(rt)).filter((rt): rt is RichText => Boolean(rt))

  const airlineCompatibilityListing = section.airlineCompatibilityListing.map(listing => ({
    title: listing.title ?? '',
    airlineCompatibilitySelections: listing.airlineCompatibilitySelections.map(selection => ({
      // The modal only reads airlineName + compatibility; `title` isn't in the proto, so default it.
      title: '',
      airlineName: selection.airlineName ?? '',
      compatibility: selection.compatibility ?? false,
    })),
  }))

  return new CustomSection({
    // Required ICustomSection fields the modal doesn't use — safe empty defaults.
    id: section.id ?? '',
    classNames: [],
    influencers: [],
    layoutType: LAYOUT_TYPES.DEFAULT,
    baseDynamicClasses: [],
    mobileDynamicClassesOverrides: [],
    classNamesMobileOverrides: [],
    // Fields the AirlineCompatibility modal actually reads.
    displayTitle: section.displayTitle,
    sectionType: CUSTOM_SECTION_TYPE.AIRLINE_COMPATIBILITY_LISTING,
    richTexts,
    airlineCompatibilityListing,
  })
}
